import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Key, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { credentialDisplayName, partnerApi, truncateCredentialId, type ApiError } from '../../lib/api';
import { AiToolsTabs } from '../ai-tools/AiToolsTabs';
import { CredentialCreateDialog } from './CredentialCreateDialog';

export const CredentialsListPage = () => {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-credentials'],
    queryFn: () => partnerApi.getCredentials(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => partnerApi.deleteCredential(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-credentials'] }),
    onError: (err: ApiError) => {
      if (err.response?.status === 409) {
        const tools = (err.response.data?.data as { tools?: { name: string }[] } | undefined)?.tools;
        const names = tools?.map(t => t.name).join(', ') ?? 'one or more tools';
        setConflict(`Cannot delete: still in use by ${names}.`);
      } else {
        setConflict(err.message);
      }
    },
  });

  const credentials = data?.credentials ?? [];

  return (
    <div className="container mx-auto max-w-5xl p-6">
      <AiToolsTabs active="credentials" />
      <PageHeader
        title="Agents"
        description="API keys and bearer tokens for APIs called by your tools. Stored securely."
        right={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            New credential
          </button>
        }
      />

      {conflict && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {conflict}
          <button type="button" className="ml-2 underline" onClick={() => setConflict(null)}>
            Dismiss
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="mt-6 text-muted-foreground">Loading…</div>
      ) : credentials.length === 0 ? (
        <div className="mt-6 rounded-md border border-dashed p-12 text-center">
          <Key className="mx-auto h-10 w-10 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No credentials yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add your first API key or bearer token. Credentials are referenced from tool definitions by
            ID.
          </p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            Create credential
          </button>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {credentials.map(c => (
                <tr key={c.id} className="border-t hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{credentialDisplayName(c)}</div>
                    {c.name && (
                      <div className="font-mono text-xs text-muted-foreground">
                        {truncateCredentialId(c.id)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{c.credential_type}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete credential ${c.id.slice(0, 8)}…?`)) {
                          deleteMutation.mutate(c.id);
                        }
                      }}
                      className="inline-flex items-center text-sm text-destructive hover:underline"
                      aria-label={`Delete ${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && <CredentialCreateDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
};
